import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Monthly.css";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { normalizeDeliveryAppKey } from "../../../utils/integrations";
import {
  createGrantTotalPercent,
  createServiceChargePercent,
  formatter,
  formatter2,
  monthNames,
} from "../../../utils/helpers";
import { MonthlyExpand } from "./monthlyExpand/MonthlyExpand";
import { translations } from "../../../utils/lang";
import { MONTHLY_FIELDS2 } from "../../../utils/Monthly";
import { exportToExcel } from "../../../utils/exportExcel";
import {
  deleteMonthlyByIdsAPI,
  fetchMonthlysAPI,
} from "../../../utils/api/report";
import { DeleteModal } from "../../modals/Delete";
import {
  ArrowDown,
  ArrowUp,
  Button,
  CloudDownload,
  Input,
  SearchCheckList,
  Trash2,
} from "../../ui";
import { groupBy, pruneSelection, rowToggleProps } from "../reportUtils";
import { getReportText } from "../reportText";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { MonthlyPaged } from "./MonthlyPaged";

const MonthlyUnpaged = () => {
  const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
  const { can, canForAll } = useRestaurantAccess();
  const [monthly, setmonthly] = useState([]);
  const [searchText, setSearchText] = useState({ date: "", other: "" });
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState("");
  const text = getReportText(lang);
  // The context recreates these on every provider render; a ref keeps the
  // fetcher stable so it runs once on mount.
  const contextRef = useRef({ storeAccess, handleTokenExpiration });
  contextRef.current = { storeAccess, handleTokenExpiration };
  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const maps = useMemo(
    () => ({
      app: translations[lang].filter.apps,
    }),
    [lang],
  );

  const checkboxFields = useMemo(
    () => [MONTHLY_FIELDS2.app, MONTHLY_FIELDS2.storeName, MONTHLY_FIELDS2.branchName],
    [],
  );

  const handleCardClick = (summary) => {
    const key = summary.store;
    setExpandedKey(expandedKey === key ? null : key);
  };

  const fetchMonthly = useCallback(async () => {
    const { storeAccess: access, handleTokenExpiration: expire } =
      contextRef.current;
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const result = await fetchMonthlysAPI(token, access);
        // Existing behaviour: the helper can't tell an expired session from
        // other failures, and a failed load is treated as expiry.
        if (result) setmonthly(result);
        else expire();
      } else {
        expire();
      }
    } catch (error) {
      console.error("Fetching failed monthly:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthly();
  }, [fetchMonthly]);

  const uniqueOptionsMap = useMemo(() => {
    return checkboxFields.reduce((acc, field) => {
      const set = new Set(monthly.map((item) => item[field]).filter(Boolean));
      acc[field] = Array.from(set);
      return acc;
    }, {});
  }, [monthly, checkboxFields]);

  const getOptionLabel = (field, val) => {
    if (field === MONTHLY_FIELDS2.app) {
      return (
        maps[field]?.[val] ??
        translations[lang].notApp + " " + val[val.length - 1]
      );
    }

    return maps[field]?.[val] ?? val;
  };

  const filteredBranches = useMemo(() => {
    const selectedStores = checkboxFilters[MONTHLY_FIELDS2.storeName] || [];

    if (selectedStores.length === 0) {
      return uniqueOptionsMap[MONTHLY_FIELDS2.branchName] || [];
    }

    const branches = new Set(
      monthly
        .filter((item) => selectedStores.includes(item[MONTHLY_FIELDS2.storeName]))
        .map((item) => item[MONTHLY_FIELDS2.branchName])
        .filter(Boolean),
    );

    return Array.from(branches);
  }, [checkboxFilters, monthly, uniqueOptionsMap]);

  const getOptions = (field) => {
    const values =
      field === MONTHLY_FIELDS2.branchName
        ? filteredBranches
        : uniqueOptionsMap[field] || [];

    return values.map((val) => ({
      value: val,
      label: getOptionLabel(field, val),
    }));
  };

  const filteredMonthly = useMemo(() => {
    return monthly.filter((item) => {
      if (searchText.date) {
        const [year, monthNum] = searchText.date.split("-");
        const selectedMonthName = monthNames[monthNum];

        // Rows carry the reporting period as year/month; rows stored before
        // the v2 migration only have the display date "April-2025".
        if (item.year && item.month) {
          if (Number(item.year) !== Number(year) || Number(item.month) !== Number(monthNum))
            return false;
        } else {
          const [itemMonth, itemYear] = String(item[MONTHLY_FIELDS2.date] || "").split("-");
          if (!itemMonth || itemMonth !== selectedMonthName || itemYear !== year)
            return false;
        }
      }

      for (const field of checkboxFields) {
        const active = checkboxFilters[field];
        if (active?.length && !active.includes(item[field])) return false;
      }

      return true;
    });
  }, [checkboxFields, checkboxFilters, monthly, searchText]);

  const summarizedData = useMemo(() => {
    const groupedSummary = {};

    filteredMonthly.forEach((item) => {
      const store = item[MONTHLY_FIELDS2.storeName];
      const app = item[MONTHLY_FIELDS2.app];
      const groupKey = store;

      if (!groupedSummary[groupKey]) {
        groupedSummary[groupKey] = {
          store: store,
          completed: 0,
          completedAmount: 0,
          rejected: 0,
          rejectedAmount: 0,
          canceled: 0,
          canceledAmount: 0,
          endurance: 0,
          refund: 0,
          ccAmount: 0,
          deliveryOffer: 0,
          keetaDeliveryOffer: 0,
          offers: 0,
          paidCampains: 0,
          serviceCharge: 0,
          enduranceAmount: 0,
          refundAmount: 0,
          posDevice: 0,
          vat: 0,
          grandTotal: 0,
        };
      }

      groupedSummary[groupKey].completed += parseFloat(
        item[MONTHLY_FIELDS2.completed] || 0,
      );
      groupedSummary[groupKey].completedAmount += parseFloat(
        item[MONTHLY_FIELDS2.completedAmount] || 0,
      );
      groupedSummary[groupKey].vat += parseFloat(
        item[MONTHLY_FIELDS2.vat] || 0,
      );
      groupedSummary[groupKey].grandTotal += parseFloat(
        item[MONTHLY_FIELDS2.grandTotal] || 0,
      );
      groupedSummary[groupKey].rejected +=
        parseFloat(item[MONTHLY_FIELDS2.rejected] || 0) || 0;
      groupedSummary[groupKey].rejectedAmount +=
        parseFloat(item[MONTHLY_FIELDS2.rejectedAmount] || 0) || 0;
      groupedSummary[groupKey].canceled += parseFloat(
        item[MONTHLY_FIELDS2.canceled] || 0,
      );
      groupedSummary[groupKey].canceledAmount += parseFloat(
        item[MONTHLY_FIELDS2.canceledAmount] || 0,
      );
      groupedSummary[groupKey].refund += parseFloat(
        item[MONTHLY_FIELDS2.refund] || 0,
      );
      groupedSummary[groupKey].endurance += parseFloat(
        item[MONTHLY_FIELDS2.endurance] || 0,
      );
      groupedSummary[groupKey].enduranceAmount += parseFloat(
        item[MONTHLY_FIELDS2.enduranceAmount] || 0,
      );
      groupedSummary[groupKey].refundAmount += parseFloat(
        item[MONTHLY_FIELDS2.refundAmount] || 0,
      );
      groupedSummary[groupKey].serviceCharge += parseFloat(
        item[MONTHLY_FIELDS2.serviceCharge] || 0,
      );
      groupedSummary[groupKey].deliveryOffer += parseFloat(
        item[MONTHLY_FIELDS2.deliveryOffer] || 0,
      );
      groupedSummary[groupKey].keetaDeliveryOffer +=
        normalizeDeliveryAppKey(app) === "keeta"
          ? parseFloat(item[MONTHLY_FIELDS2.deliveryOffer] || 0)
          : 0;
      groupedSummary[groupKey].offers += parseFloat(item.offers || 0);
      groupedSummary[groupKey].paidCampains += parseFloat(
        item.paidCampains || 0,
      );
      groupedSummary[groupKey].ccAmount += parseFloat(
        item[MONTHLY_FIELDS2.ccAmount] || 0,
      );
    });

    return Object.values(groupedSummary);
  }, [filteredMonthly]);

  // Rows per store, built once (each summary row used to re-filter the whole
  // filtered dataset).
  const rowsByStore = useMemo(
    () => groupBy(filteredMonthly, (row) => row[MONTHLY_FIELDS2.storeName]),
    [filteredMonthly],
  );

  const deleteByIdsName = async () => {
    const token = localStorage.getItem("auth-token");
    const ids = filteredMonthly.map((item) => item._id);

    if (!ids.length) return;
    // Handler-level authorization: the bulk-delete permission and every affected
    // restaurant must pass (rows carry the restaurant _id).
    if (!can("report.bulkDelete") || !canForAll("report.entry.delete", filteredMonthly.map((item) => item.storeId))) return;
    if (token) {
      const result = await deleteMonthlyByIdsAPI(token, ids);
      if (result) {
        setDeleteTarget(null);
        setDeleteError("");
        fetchMonthly();
      } else {
        // Keep the dialog open so the failure is visible and can be retried.
        setDeleteError(text.actionFailed);
      }
    } else {
      handleTokenExpiration();
    }
  };

  // Changing stores clears branch selections that no longer belong to any
  // selected store (they kept filtering while their tags showed no label).
  const handleCheckboxChange = (field, selected) => {
    setCheckboxFilters((prev) => {
      const next = { ...prev, [field]: selected };
      if (field !== MONTHLY_FIELDS2.storeName) return next;
      const selectedStores = next[MONTHLY_FIELDS2.storeName] || [];
      const available = new Set(
        monthly
          .filter(
            (item) =>
              !selectedStores.length ||
              selectedStores.includes(item[MONTHLY_FIELDS2.storeName]),
          )
          .map((item) => item[MONTHLY_FIELDS2.branchName]),
      );
      next[MONTHLY_FIELDS2.branchName] = pruneSelection(prev[MONTHLY_FIELDS2.branchName], [...available]);
      return next;
    });
  };

  const hasActiveFilters =
    Boolean(searchText.date) ||
    Object.values(checkboxFilters).some((values) => values?.length);

  const handleClearFilters = () => {
    setSearchText({ date: "", other: "" });
    setCheckboxFilters({});
  };

  return (
    <div className="report-monthly-table">
      <div className="report-monthly-search-panel">
        <div className="report-monthly-text-filter">
          <Input
            type="month"
            fullWidth={false}
            label={translations[lang].reports.monthly.inputTitles[0]}
            value={searchText.date || ""}
            onChange={(e) =>
              setSearchText((prev) => ({ ...prev, date: e.target.value }))
            }
          />

          {checkboxFields.map((field, i) => (
            <SearchCheckList
              key={field}
              placeholder={
                translations[lang].reports.monthly.inputTitles[i + 1]
              }
              options={getOptions(field)}
              selected={checkboxFilters[field] || []}
              onChange={(selected) => handleCheckboxChange(field, selected)}
            />
          ))}
        </div>

        <div className="report-filter-actions">
          <Button
            variant="warning"
            appearance="soft"
            size="sm"
            onClick={handleClearFilters}
          >
            {translations[lang].reports.monthly.inputTitles[4]}
          </Button>
            <div className="exportToExcel">
              <Button
                className="report-export-btn"
                variant="primary"
                appearance="soft"
                size="sm"
                icon={<CloudDownload />}
                iconPosition="end"
                aria-label={text.exportLabel(filteredMonthly.length)}
                title={text.exportLabel(filteredMonthly.length)}
                disabled={!filteredMonthly.length}
                onClick={() => exportToExcel(filteredMonthly, "Monthly")}
              >
                {text.export}
              </Button>
            </div>

          {can("report.bulkDelete") && (
            <div
              className="delete-by-container"
              style={{ marginInlineStart: "auto" }}
            >
              <Button
                variant="danger"
                appearance=""
                size="sm"
                rounded
                icon={<Trash2 size={18} />}
                title={text.deleteFiltered}
                aria-label={text.deleteFiltered}
                disabled={!filteredMonthly.length}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(true);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="report-monthly-card-col">
        {loading && (
          <p className="report-state" role="status">
            {text.loading}
          </p>
        )}
        {!loading && summarizedData.length === 0 && (
          <p className="report-state">{text.empty}</p>
        )}
        {summarizedData.map((summary) => {
          const key = summary.store;
          const isExpanded = key === expandedKey;
          const filtered = rowsByStore.get(summary.store) || [];
          const isApproved = filtered.find((a) => a.isApproved === false);

          return (
            <React.Fragment key={key}>
              <div
                className={`report-monthly-summary-card ${
                  isExpanded ? "colored" : ""
                }  ${
                  isApproved && !isApproved?.isApproved ? "notApproved" : ""
                }`}
                {...rowToggleProps(isExpanded, () => handleCardClick(summary))}
              >
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p center">
                    <p>
                      <strong>{summary.store}</strong>
                    </p>
                    {isApproved && (
                      <span className="report-pending-tag">{text.pendingApproval}</span>
                    )}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[0]}
                    </strong>{" "}
                    {formatter2.format(summary.completed)} /{" "}
                    {formatter.format(summary.completedAmount.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[1]}
                    </strong>{" "}
                    {formatter.format(summary.vat.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[2]}
                    </strong>{" "}
                    (
                    {createGrantTotalPercent({
                      completedAmount: summary.completedAmount,
                      grandTotal: summary.grandTotal - summary.paidCampains,
                      offers: summary.offers,
                    })}
                    %) /{" "}
                    {formatter.format(
                      summary.grandTotal.toFixed(2) -
                        summary.paidCampains.toFixed(2),
                    )}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[3]}
                        </strong>{" "}
                        {formatter2.format(summary.rejected)} /{" "}
                        {formatter.format(summary.rejectedAmount)}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[4]}
                        </strong>{" "}
                        {formatter2.format(summary.canceled)} /{" "}
                        {formatter.format(summary.canceledAmount)}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[5]}
                        </strong>{" "}
                        {formatter2.format(summary.endurance)} /{" "}
                        {formatter.format(summary.enduranceAmount.toFixed(2))}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[6]}
                        </strong>{" "}
                        {formatter2.format(summary.refund)} /{" "}
                        {formatter.format(summary.refundAmount.toFixed(2))}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[9]}
                    </strong>{" "}
                    {formatter.format(summary.ccAmount.toFixed(2))}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[7]}
                    </strong>{" "}
                    (
                    {createServiceChargePercent({
                      completedAmount: summary.completedAmount,
                      serviceCharge: summary.serviceCharge,
                      offers: summary.offers,
                      deliveryOffer: summary.keetaDeliveryOffer,
                    })}
                    %) / {formatter.format(summary.serviceCharge.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[8]}
                        </strong>
                        (
                        {summary.completed > 0
                          ? (summary.deliveryOffer / summary.completed).toFixed(
                              2,
                            )
                          : 0}
                        ) / {formatter.format(summary.deliveryOffer.toFixed(2))}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[10]}
                        </strong>
                        (
                        {summary.completed > 0
                          ? (summary.offers / summary.completed).toFixed(2)
                          : 0}
                        ) / {formatter.format(summary.offers.toFixed(2))}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[11]}
                    </strong>{" "}
                    {formatter.format(summary.paidCampains.toFixed(2))}
                  </div>
                </div>
                <div className="report-expand-icon">
                  {isExpanded ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </div>
              </div>
              {isExpanded && (
                <MonthlyExpand
                  filteredData={filtered}
                  onRefresh={fetchMonthly}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {deleteTarget && (
        <DeleteModal
          cancel={() => {
            setDeleteTarget(null);
            setDeleteError("");
          }}
          id={deleteTarget}
          handleDelete={deleteByIdsName}
          error={deleteError}
          description={`${text.deleteFilteredDescription(filteredMonthly.length)}${
            hasActiveFilters ? "" : ` ${text.deleteAllWarning}`
          }`}
        />
      )}
    </div>
  );
};

// PAGED_REPORTS on: server-side filtering, sorting and paging; off (default): the table above, unchanged.
export const Monthly = ({ active = true }) =>
  isFeatureEnabled(FEATURE_NAMES.PAGED_REPORTS) ? <MonthlyPaged active={active} /> : <MonthlyUnpaged />;
